/** Shared types and helpers for the merchant configuration editors (wizard and hub Configuration pages). */

export const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
export const CSS_LENGTH_RE = /^\d{1,3}(\.\d{1,2})?(px|rem|em)$/;

export type Option = { value: string; label: string };

/** Mirrors `payment_links/services/card_checkout_settings.py`. */
export const PAYMENT_TYPES: readonly Option[] = [
  ['PANENTRY', 'Card number entry'],
  ['CLICKTOPAY', 'Click to Pay'],
  ['GOOGLEPAY', 'Google Pay'],
  ['APPLEPAY', 'Apple Pay'],
  ['PAZE', 'Paze'],
  ['PAYPAL', 'PayPal'],
  ['VENMO', 'Venmo'],
  ['AFFIRM', 'Affirm'],
  ['AFTERPAY', 'Afterpay'],
].map(([value, label]) => ({ value, label }));

export const CARD_NETWORKS: readonly Option[] = [
  'VISA', 'MASTERCARD', 'AMEX', 'CARNET', 'CARTESBANCAIRES', 'CUP', 'DINERSCLUB', 'DISCOVER', 'EFTPOS',
  'ELO', 'JAYWAN', 'JCB', 'JCREW', 'KCP', 'MADA', 'MAESTRO', 'MEEZA', 'PAYPAK', 'UATP',
].map((value) => ({ value, label: value }));

export const BUTTON_TYPES: readonly Option[] = [
  'ADD_CARD', 'CARD_PAYMENT', 'CHECKOUT', 'CHECKOUT_AND_CONTINUE', 'DEBIT_CREDIT', 'DONATE', 'PAY',
  'PAY_WITH_CARD', 'SAVE_CARD', 'SUBSCRIBE_WITH_CARD',
].map((value) => ({ value, label: value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()) }));

export const BILLING_TYPES: readonly Option[] = [
  { value: 'FULL', label: 'Full billing address' },
  { value: 'PARTIAL', label: 'Partial (country and postal code)' },
  { value: 'NONE', label: 'None' },
];

export const THEMES: readonly Option[] = [{ value: 'LIGHT', label: 'Light' }];

export const BOOLEAN_KEYS: ReadonlyArray<{ key: BooleanKey; label: string; hint: string }> = [
  { key: 'request_email', label: 'Ask for email', hint: 'Collect the payer email on the card form.' },
  { key: 'request_phone', label: 'Ask for phone', hint: 'Collect the payer phone number.' },
  { key: 'show_confirmation_step', label: 'Confirmation step', hint: 'Show a review step before paying.' },
  { key: 'show_network_icons', label: 'Show network icons', hint: 'Display accepted card network logos.' },
  { key: 'request_save_credentials', label: 'Offer to save card', hint: 'Let the payer save credentials.' },
];

export const SUB_MERCHANT_FIELDS: ReadonlyArray<{ key: SubMerchantKey; label: string; max: number }> = [
  { key: 'id', label: 'Sub-merchant ID', max: 20 },
  { key: 'card_acceptor_id', label: 'Card acceptor ID', max: 15 },
  { key: 'name', label: 'Name', max: 37 },
  { key: 'address1', label: 'Address', max: 38 },
  { key: 'locality', label: 'City', max: 21 },
  { key: 'administrative_area', label: 'State / region', max: 50 },
  { key: 'postal_code', label: 'Postal code', max: 15 },
];

export const DESCRIPTOR_NAME_MAX = 25;
/** Backend `_SAFE_TEXT_RE`. */
export const SAFE_TEXT_RE = /^[\w .,'&@#/()+-]*$/u;

export type BooleanKey =
  | 'request_email'
  | 'request_phone'
  | 'show_confirmation_step'
  | 'show_network_icons'
  | 'request_save_credentials';
export type SubMerchantKey =
  | 'id'
  | 'card_acceptor_id'
  | 'name'
  | 'address1'
  | 'locality'
  | 'administrative_area'
  | 'postal_code';

/** Whitelisted Cybersource appearance variables (mirrors `cybersource_appearance.py`). */
const INPUT_STATES = ['', 'Hover', 'Focused', 'Active', 'Pressed', 'Error', 'Valid'];
const INPUT_PARTS = ['Background', 'Color', 'PlaceholderColor', 'BorderColor'];
const BUTTON_STATES = ['', 'Hover', 'Focus', 'Active', 'Disabled'];
const BUTTON_PARTS = ['Background', 'Foreground', 'BorderColor'];

export interface AppearanceGroup {
  title: string;
  keys: string[];
}

export const APPEARANCE_COLOR_GROUPS: readonly AppearanceGroup[] = [
  {
    title: 'Page and header',
    keys: [
      'backgroundColor', 'textColor', 'headerBackground', 'headerForeground',
      'headerAvatarBackgroundColor', 'headerAvatarForegroundColor', 'paymentSelectionBackground',
    ],
  },
  {
    title: 'Buttons',
    keys: BUTTON_STATES.flatMap((state) => BUTTON_PARTS.map((part) => `button${state}${part}`)),
  },
  {
    title: 'Inputs',
    keys: INPUT_STATES.flatMap((state) => INPUT_PARTS.map((part) => `input${state}${part}`)),
  },
];

export const APPEARANCE_LENGTH_KEYS = ['borderRadius', 'inputBorderRadius', 'buttonBorderRadius'];
export const BORDER_STYLES = ['solid', 'dashed', 'dotted', 'double', 'none'];
export const BUTTON_SHAPES = ['filled', 'outlined'];
export const APPEARANCE_STYLE_KEYS = ['inputBorderStyle', 'buttonBorderStyle'];

/** `Merchant.card_checkout_settings`; every key optional, absent means "use the provider default". */
export interface CardCheckoutSettings {
  payment_types?: string[];
  card_networks?: string[];
  button_type?: string;
  billing_type?: string;
  theme?: string;
  descriptor_name?: string;
  sub_merchant?: Partial<Record<SubMerchantKey, string>>;
  appearance_overrides?: Record<string, string>;
  request_email?: boolean;
  request_phone?: boolean;
  show_confirmation_step?: boolean;
  show_network_icons?: boolean;
  request_save_credentials?: boolean;
}

/** Drop empty strings, empty lists and empty objects so the stored JSON stays minimal and valid. */
export function cleanCardSettings(raw: CardCheckoutSettings): CardCheckoutSettings {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && !value.length) continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      const inner = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== '' && v != null));
      if (!Object.keys(inner).length) continue;
      out[key] = inner;
      continue;
    }
    out[key] = value;
  }
  return out as CardCheckoutSettings;
}

/** Client-side mirror of the server rules; returns human-readable problems. */
export function validateCardSettings(settings: CardCheckoutSettings): string[] {
  const errors: string[] = [];
  const name = settings.descriptor_name;
  if (name && (name.length > DESCRIPTOR_NAME_MAX || !SAFE_TEXT_RE.test(name))) {
    errors.push(`Descriptor name must be plain text of at most ${DESCRIPTOR_NAME_MAX} characters.`);
  }
  for (const field of SUB_MERCHANT_FIELDS) {
    const text = settings.sub_merchant?.[field.key];
    if (text && (text.length > field.max || !SAFE_TEXT_RE.test(text))) {
      errors.push(`${field.label} must be plain text of at most ${field.max} characters.`);
    }
  }
  for (const [key, value] of Object.entries(settings.appearance_overrides ?? {})) {
    const isLength = APPEARANCE_LENGTH_KEYS.includes(key);
    const isText = key === 'fontFamily' || key.endsWith('Style') || key === 'buttonShape';
    if (!isLength && !isText && !HEX_COLOR_RE.test(value)) {
      errors.push(`${key} must be a #RRGGBB colour.`);
    }
    if (isLength && !CSS_LENGTH_RE.test(value)) {
      errors.push(`${key} must be a length such as 8px or 0.5rem.`);
    }
  }
  return errors;
}

/** Payment-link methods a merchant can have switched off. */
export const PAYMENT_LINK_METHODS: readonly Option[] = [
  { value: 'card', label: 'Card' },
  { value: 'mobile_money', label: 'Mobile money' },
];

export const FEE_BEARERS: readonly Option[] = [
  { value: 'MERCHANT', label: 'Merchant absorbs fees' },
  { value: 'CUSTOMER', label: 'Customer pays fees' },
];

export const DEFAULT_LOCALES: readonly Option[] = [
  { value: 'en', label: 'English' },
  { value: 'fr', label: 'French' },
];

/** Checkout appearance values (`checkout_*` keys of the merchant). */
export interface CheckoutAppearanceValue {
  checkout_primary_color: string;
  checkout_secondary_color: string;
  checkout_accent_color: string;
  checkout_background_color: string;
  checkout_default_locale: string;
  checkout_custom_css: string;
}

export interface PaymentOptionsValue {
  fee_bearer: string;
  allow_merchant_fees: boolean;
  checkout_card_enabled: boolean;
  checkout_mainmoney_enabled: boolean;
  payment_link_disabled_methods: string[];
}

export interface CountryAccessValue {
  allowed: string[];
  disallowed: string[];
}

export const DEFAULT_CHECKOUT_APPEARANCE: CheckoutAppearanceValue = {
  checkout_primary_color: '#ff3366',
  checkout_secondary_color: '#5f5e5e',
  checkout_accent_color: '#b90040',
  checkout_background_color: '#f8f9fb',
  checkout_default_locale: 'en',
  checkout_custom_css: '',
};

export const DEFAULT_PAYMENT_OPTIONS: PaymentOptionsValue = {
  fee_bearer: 'MERCHANT',
  allow_merchant_fees: false,
  checkout_card_enabled: false,
  checkout_mainmoney_enabled: false,
  payment_link_disabled_methods: [],
};

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const IPV6_RE = /^[0-9a-fA-F:]+$/;

/** Client-side check of an IP or CIDR entry (the API stays authoritative). */
export function isValidIpOrCidr(entry: string): boolean {
  const text = entry.trim();
  const [address, prefix, extra] = text.split('/');
  if (!address || extra !== undefined) return false;
  const isV4 = IPV4_RE.test(address);
  const isV6 = !isV4 && address.includes(':') && IPV6_RE.test(address) && address.split(':').length <= 8;
  if (!isV4 && !isV6) return false;
  if (prefix === undefined) return true;
  const bits = Number(prefix);
  return /^\d{1,3}$/.test(prefix) && bits >= 0 && bits <= (isV4 ? 32 : 128);
}

export const ALL_SCOPES = 'all_scopes';
export const APPLICATION_ENVIRONMENTS: readonly Option[] = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'development', label: 'Development' },
  { value: 'production', label: 'Production' },
];

/** Editable fields of a merchant application (create and edit forms). */
export interface ApplicationFormValue {
  name: string;
  description: string;
  environment: string;
  allowed_ips: string[];
  allowed_scopes: string[];
  is_active: boolean;
}

export function emptyApplicationForm(): ApplicationFormValue {
  return { name: '', description: '', environment: 'sandbox', allowed_ips: [], allowed_scopes: [], is_active: true };
}

/** Field → message; empty object when valid. */
export function validateApplicationForm(value: ApplicationFormValue): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!value.name.trim()) errors['name'] = 'Required.';
  const bad = value.allowed_ips.map((ip) => ip.trim()).filter((ip) => ip && !isValidIpOrCidr(ip));
  if (bad.length) errors['allowed_ips'] = `Invalid IP or CIDR: ${bad.join(', ')}`;
  return errors;
}

/** Request body for create/update: blank IPs dropped, `all_scopes` is exclusive. */
export function applicationPayload(value: ApplicationFormValue): ApplicationFormValue {
  const scopes = value.allowed_scopes.includes(ALL_SCOPES) ? [ALL_SCOPES] : value.allowed_scopes;
  return {
    ...value,
    name: value.name.trim(),
    description: value.description.trim(),
    allowed_ips: value.allowed_ips.map((ip) => ip.trim()).filter(Boolean),
    allowed_scopes: scopes,
  };
}
